import { useMemo } from 'react'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from 'chart.js'
import { Bar } from 'react-chartjs-2'
import type { TopDrink } from '@/utils/analytics'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip)

interface TopDrinksChartProps {
  drinks: TopDrink[]
}

export default function TopDrinksChart({ drinks }: TopDrinksChartProps) {
  const data = useMemo(() => ({
    labels: drinks.map(d => d.name),
    datasets: [
      {
        label: 'Drinks',
        data: drinks.map(d => d.count),
        backgroundColor: '#2dd4bf',
        borderRadius: 3,
      },
    ],
  }), [drinks])

  const options = useMemo(() => ({
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgb(30, 41, 59)',
        titleColor: 'rgb(248, 250, 252)',
        bodyColor: 'rgb(148, 163, 184)',
        borderColor: 'rgb(71, 85, 105)',
        borderWidth: 1,
        callbacks: {
          label: (item: { dataIndex: number; parsed: { x: number } }) => {
            const d = drinks[item.dataIndex]
            if (!d) return ''
            return [
              `${item.parsed.x} time${item.parsed.x === 1 ? '' : 's'}`,
              `${d.totalStdDrinks.toFixed(1)} std drinks`,
              `${Math.round(d.totalMl).toLocaleString()} mL total`,
              `${Math.round(d.totalCalories).toLocaleString()} kcal total`,
            ]
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: { color: 'rgb(148, 163, 184)', precision: 0 },
        grid: { color: 'rgba(71, 85, 105, 0.3)' },
      },
      y: {
        ticks: {
          color: 'rgb(148, 163, 184)',
          callback: function(this: { getLabelForValue: (i: number) => string }, _v: string | number, i: number) {
            const label = this.getLabelForValue(i)
            return label.length > 26 ? label.slice(0, 24) + '…' : label
          },
        },
        grid: { display: false },
      },
    },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }), [drinks]) as any

  if (drinks.length === 0) {
    return (
      <div className="card">
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-2">Top drinks</h2>
        <p className="text-sm text-[var(--text-secondary)]">No data in this period.</p>
      </div>
    )
  }

  // Height scales with row count so labels never overlap
  const height = Math.max(180, drinks.length * 28 + 40)

  return (
    <div className="card">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-semibold text-[var(--text-primary)]">Top drinks</h2>
        <p className="text-xs text-[var(--text-secondary)]">most frequent</p>
      </div>
      <div style={{ height }}>
        <Bar data={data} options={options} />
      </div>
    </div>
  )
}
